import { Card } from "gen/payments/models_pb"
import {
  TenderSubmissionMetadata,
  TenderSubmissionMetadata_SavedCard,
  TenderSubmissionMetadata_SubmissionType,
} from "gen/txn/models_pb"
import { Money } from "shared-ts/classes/money"
import { UUID } from "shared-ts/classes/uuid"
import { OptionTypes } from "shared-ts/types/tender/tender.types"

import {
  SubmissionOption,
  CardSubOptionTypes,
  ACHSubOptionTypes,
} from "./drawerState.types"

interface DisplayableSavedCard extends TenderSubmissionMetadata_SavedCard {
  card?: Card
}

export interface DisplayableTenderSubmissionMetadata
  extends Omit<TenderSubmissionMetadata, "savedCard"> {
  savedCard?: DisplayableSavedCard
}

export function makeTenderSubmissionMetadata(
  submissionState: SubmissionOption,
  amount: Money,
  cashRegisterId: UUID | undefined,
  cardReaderId: string
): DisplayableTenderSubmissionMetadata {
  const baseMetadata = {
    amount: amount,
    tenderMethodId: submissionState.optionID,
    surchargeTaxByTaxRateId: {},
  }

  switch (submissionState.type) {
    case OptionTypes.CASH:
      return {
        ...baseMetadata,
        submissionType: TenderSubmissionMetadata_SubmissionType.CASH,
        cash: {
          cashReceived: submissionState.cashReceived,
          cashRegisterId: cashRegisterId,
          ...(submissionState.cashRoundingDelta?.isNonZero()
            ? { cashRoundingDelta: submissionState.cashRoundingDelta }
            : {}),
        },
      }
    case OptionTypes.CHECK:
      return {
        ...baseMetadata,
        submissionType: TenderSubmissionMetadata_SubmissionType.CHECK,
        check: {
          checkNumber: submissionState.checkNumber,
        },
      }
    case OptionTypes.CHARGE_ACCOUNT:
      return {
        ...baseMetadata,
        submissionType: TenderSubmissionMetadata_SubmissionType.CHARGE_ACCOUNT,
        chargeAccount: {
          cashRegisterId: cashRegisterId,
        },
      }
    case OptionTypes.CUSTOM:
      return {
        ...baseMetadata,
        submissionType: TenderSubmissionMetadata_SubmissionType.CUSTOM,
        custom: {
          note: submissionState.note,
        },
      }
    case OptionTypes.CARD:
      {
        const baseCardSubmission = {
          ...baseMetadata,
          surcharge: submissionState.cardSurchargeAmount,
          surchargeTax: submissionState.cardSurchargeTax,
          surchargeTaxByTaxRateId: submissionState.cardSurchargeTaxByTaxRateId,
        }
        switch (submissionState.subtype) {
          case CardSubOptionTypes.CARD_PRESENT:
            return {
              ...baseCardSubmission,
              submissionType:
                TenderSubmissionMetadata_SubmissionType.PRESENT_CARD,
              presentCard: {
                stripeReaderId: cardReaderId,
                shouldSaveCard: submissionState.shouldSaveCard,
                nickname: submissionState.cardNickName,
                stripePaymentIntentId: "",
                setIsPrimary: false,
                isInterac: submissionState.isInterac ?? false,
              },
            }
          case CardSubOptionTypes.SAVED_CARD:
            return {
              ...baseCardSubmission,
              submissionType:
                TenderSubmissionMetadata_SubmissionType.SAVED_CARD,
              savedCard: {
                card: submissionState.card,
                stripePaymentMethodId: submissionState.card?.id ?? "",
                stripePaymentIntentId: "",
                setIsPrimary: false,
                shouldSaveCard: false,
                nickname: submissionState.card?.nickname ?? "",
              },
            }
          case CardSubOptionTypes.MANUALLY_ENTERED_CARD:
            return {
              ...baseCardSubmission,
              submissionType:
                TenderSubmissionMetadata_SubmissionType.MANUALLY_ENTERED_CARD,
              manuallyEnteredCard: {
                card: {
                  cardNumber: submissionState.manuallyEnteredCard.number,
                  expirationMonth:
                    submissionState.manuallyEnteredCard.expirationMonth,
                  expirationYear:
                    submissionState.manuallyEnteredCard.expirationYear,
                  cvc: submissionState.manuallyEnteredCard.cvc,
                  zipCode: submissionState.manuallyEnteredCard.zip,
                },
                shouldSaveCard: submissionState.shouldSaveCard,
                nickname: submissionState.cardNickName,
                stripePaymentIntentId: "",
                setIsPrimary: false,
              },
            }
          case CardSubOptionTypes.PREVIOUS_CARD:
            return {
              ...baseCardSubmission,
              submissionType:
                TenderSubmissionMetadata_SubmissionType.PREVIOUS_CARD,
            }
          case CardSubOptionTypes.PREVIOUS_INTERAC_CARD:
            return {
              ...baseCardSubmission,
              submissionType:
                TenderSubmissionMetadata_SubmissionType.PREVIOUS_INTERAC_CARD,
              previousInteracCard: {
                stripeReaderId: cardReaderId,
              },
            }
        }
      }
      break
    case OptionTypes.ACH:
      switch (submissionState.subtype) {
        case ACHSubOptionTypes.SAVED_ACH:
          return {
            ...baseMetadata,
            submissionType: TenderSubmissionMetadata_SubmissionType.SAVED_ACH,
            savedAch: {
              stripePaymentMethodId:
                submissionState.bankAccount?.stripePaymentMethodId ?? "",
              nickname: submissionState.bankAccount?.nickname ?? "",
              setIsPrimary: false,
              stripePaymentIntentId: "",
            },
          }
        case ACHSubOptionTypes.PREVIOUS_ACH:
          return {
            ...baseMetadata,
            submissionType:
              TenderSubmissionMetadata_SubmissionType.PREVIOUS_ACH,
          }
      }
      break
    case OptionTypes.WRITE_OFF:
      return {
        ...baseMetadata,
        submissionType: TenderSubmissionMetadata_SubmissionType.WRITE_OFF,
      }
    case OptionTypes.GIFT_CARD:
      return {
        ...baseMetadata,
        submissionType: TenderSubmissionMetadata_SubmissionType.GIFT_CARD,
        giftCard: {
          giftCardId: submissionState.giftCardId,
        },
      }
    default: {
      const _exhaustiveCheck: never = submissionState
      throw new Error(`Unsupported option type: ${_exhaustiveCheck}`)
    }
  }
}
